import React, { useState, useCallback } from 'react';
import { Info, Scissors, ListOrdered } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import PDFViewer from '../components/common/PDFViewer';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';
import './SplitPDF.css';

type SplitMode = 'page' | 'all';

const SplitPDF: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [splitMode, setSplitMode] = useState<SplitMode>('page');
    const [refreshKey, setRefreshKey] = useState(0);
    const [splitByPageInput, setSplitByPageInput] = useState<string>('');

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = useCallback((selectedFiles: File[]) => {
        setFiles([selectedFiles[0]]);
        setSplitByPageInput('');
        setTotalPages(0);
        setProcessError(null);
    }, [setProcessError]);

    const handleTotalPagesChange = useCallback((pages: number) => {
        setTotalPages(pages);
    }, []);

    const getSplitPoints = (input: string, total: number): number[] => {
        const parts = input.split(/[,，]/);
        const cutPoints = new Set<number>();

        parts.forEach(part => {
            const trimmed = part.trim();
            if (!trimmed) return;

            if (trimmed.includes('-')) {
                const [n, m] = trimmed.split('-').map(Number);
                if (!isNaN(n) && !isNaN(m)) {
                    if (n > 1) cutPoints.add(n - 1);
                    if (m < total) cutPoints.add(m);
                }
            } else {
                const n = Number(trimmed);
                if (!isNaN(n) && n >= 1 && n < total) {
                    cutPoints.add(n);
                }
            }
        });

        return Array.from(cutPoints).sort((a, b) => a - b);
    };

    const handleSplitByPageChange = (value: string) => {
        if (value && !/^[\d,\-\s]*$/.test(value)) return;
        setSplitByPageInput(value);
    };

    const handleSplit = async () => {
        if (files.length === 0) {
            setProcessError('请选择一个 PDF 文件');
            return;
        }

        if (splitMode === 'page' && !splitByPageInput.trim()) {
            setProcessError('请输入拆分点');
            return;
        }

        let pageNumbers = '';
        if (splitMode === 'page') {
            const points = getSplitPoints(splitByPageInput, totalPages);
            if (points.length === 0) {
                setProcessError('无效的拆分点');
                return;
            }
            pageNumbers = points.join(',');
        } else {
            pageNumbers = 'all';
        }

        await executeTask(files[0], 'split', { pageNumbers });
    };

    const handleReset = () => {
        resetTask();
    };

    return (
        <div className="split-page">
            <div className="container">
                <div className="page-header">
                    <h1>拆分 PDF 文件</h1>
                    <p>选择拆分模式，将 PDF 拆分成您需要的文件</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        <div className="upload-section">
                            <FileUpload
                                onFilesSelected={handleFilesSelected}
                                multiple={false}
                                maxFiles={1}
                            />
                        </div>

                        {files.length > 0 && (
                            <div className="split-workspace">
                                {/* Left Column: Settings */}
                                <div className="split-inputs-column">
                                    <div className="inputs-header">
                                        <h3>拆分模式</h3>
                                    </div>

                                    <div className="mode-selector">
                                        <button
                                            className={`mode-btn ${splitMode === 'page' ? 'active' : ''}`}
                                            onClick={() => setSplitMode('page')}
                                            title="在指定页码处拆分"
                                            disabled={!!taskId}
                                        >
                                            <ListOrdered size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                                            按页码拆分
                                        </button>
                                        <button
                                            className={`mode-btn ${splitMode === 'all' ? 'active' : ''}`}
                                            onClick={() => setSplitMode('all')}
                                            title="每一页拆分为单独文件"
                                            disabled={!!taskId}
                                        >
                                            <Scissors size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                                            全部拆分
                                        </button>
                                    </div>

                                    <div className="inputs-header">
                                        <h3>参数设置</h3>
                                        <p className="input-hint">
                                            <Info size={14} style={{ display: 'inline', marginRight: 4 }} />
                                            {splitMode === 'page' && '使用 "n" 或 "n-m" 语法拆分'}
                                            {splitMode === 'all' && '将生成包含所有页面的 ZIP 包'}
                                        </p>
                                    </div>

                                    {/* Mode: Split by Page */}
                                    {splitMode === 'page' && (
                                        <div className="range-list" style={{ maxHeight: 'none', overflow: 'visible' }}>
                                            <div className="range-item">
                                                <div className="range-label">拆分点</div>
                                                <div style={{ marginBottom: '10px', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                                                    在指定页码处拆分 PDF。使用"n"表示在第 n 页之后拆分；使用"n-m"表示在第 n 页之前与第 m 页之后拆分。
                                                </div>
                                                <div className="range-input-wrapper">
                                                    <input
                                                        type="text"
                                                        value={splitByPageInput}
                                                        onChange={(e) => handleSplitByPageChange(e.target.value)}
                                                        placeholder="例如: 3, 7 或 3-8"
                                                        className="form-input"
                                                        disabled={!!taskId}
                                                    />
                                                </div>
                                                <div className="input-description" style={{ marginTop: '10px', fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
                                                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                                        <li style={{ marginBottom: '4px' }}>单一拆分点：3,7 （在第 3 与第 7 页后拆分）</li>
                                                        <li style={{ marginBottom: '4px' }}>范围拆分点：3-8 （在第 3 页前与第 8 页后拆分）</li>
                                                        <li>混合：2,5-10,15 （在第 2 页后、第 5 页前、第 10 页后与第 15 页后拆分）</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Mode: All */}
                                    {splitMode === 'all' && (
                                        <div className="range-list" style={{ maxHeight: 'none', overflow: 'visible' }}>
                                            <div className="range-item" style={{ textAlign: 'center', padding: '30px', color: 'var(--gray-500)' }}>
                                                <Scissors size={32} style={{ marginBottom: 10, opacity: 0.5 }} />
                                                <p style={{ margin: 0 }}>
                                                    将把 {totalPages} 页完整拆分为 {totalPages} 个独立的 PDF 文件并打包下载。
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {!taskId ? (
                                        <div className="main-action">
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleSplit}
                                                isLoading={isCreating}
                                                fullWidth
                                                icon={<Scissors />}
                                                disabled={isCreating}
                                            >
                                                {isCreating ? '准备拆分...' : '开始拆分'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: '20px' }}>
                                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                                        </div>
                                    )}

                                    {processError && !taskId && (
                                        <div className="message message-error" style={{ marginTop: '15px' }}>
                                            {processError}
                                        </div>
                                    )}
                                </div>

                                {/* Right Column: Viewer */}
                                <div className="split-viewer-column">
                                    <div className="viewer-header">
                                        <h3>页面预览</h3>
                                        <span className="page-count">共 {totalPages} 页</span>
                                    </div>
                                    <div className="viewer-container" style={{ padding: 0, height: '600px', background: 'transparent', border: 'none' }}>
                                        <PDFViewer
                                            file={files[0]}
                                            onTotalPagesChange={handleTotalPagesChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>

                    <ConversionHistory operationFilter="split" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default SplitPDF;
